import {} from '@ibe/constants';
import { ERROR_CODES, httpStatusForCode } from './codes.js';
/**
 * Base class for every error the platform raises deliberately.
 *
 * Carries the five things an operator needs to act on a failure: a registered
 * code, an HTTP status, the correlation ID tying it to a request trace, the
 * underlying cause, and structured metadata.
 *
 * Metadata must never contain personal or payment data — it is logged, and
 * audit-log minimisation is a GDPR Art. 5 and PCI DSS requirement.
 */
export class ApplicationError extends Error {
    code;
    httpStatus;
    correlationId;
    metadata;
    /** True when the client could reasonably fix the request and retry. */
    isOperational;
    constructor(code, options = {}) {
        super(options.message ?? code, options.cause === undefined ? {} : { cause: options.cause });
        this.name = new.target.name;
        this.code = code;
        this.httpStatus = options.httpStatus ?? httpStatusForCode(code);
        this.correlationId = options.correlationId;
        this.metadata = options.metadata ?? {};
        this.isOperational = options.isOperational ?? true;
        Error.captureStackTrace(this, new.target);
    }
    /**
     * Serialises to the wire format, deliberately omitting `cause` and the
     * stack. Internal detail belongs in logs, never in a client response.
     */
    toJSON() {
        return {
            code: this.code,
            message: this.message,
            correlationId: this.correlationId,
            metadata: this.metadata,
        };
    }
}
/** Request failed schema or business validation. */
export class ValidationError extends ApplicationError {
    fieldErrors;
    constructor(fieldErrors = [], options = {}) {
        super(ERROR_CODES.VALIDATION_ERROR, {
            message: 'Request validation failed',
            ...options,
        });
        this.fieldErrors = fieldErrors;
    }
}
/** Caller is not authenticated, or the credential presented is unusable. */
export class AuthenticationError extends ApplicationError {
    constructor(code = ERROR_CODES.UNAUTHORIZED, options = {}) {
        super(code, options);
    }
}
/** Caller is authenticated but not permitted to perform this action. */
export class AuthorizationError extends ApplicationError {
    constructor(code = ERROR_CODES.FORBIDDEN, options = {}) {
        super(code, options);
    }
}
export class NotFoundError extends ApplicationError {
    constructor(code = ERROR_CODES.NOT_FOUND, options = {}) {
        super(code, options);
    }
}
/** State conflict: optimistic-lock failure, duplicate key, illegal transition. */
export class ConflictError extends ApplicationError {
    constructor(code = ERROR_CODES.CONFLICT, options = {}) {
        super(code, options);
    }
}
/** Raised when the caller supplies a request body that is not valid for this endpoint,
 * or when a required configuration is missing. HTTP 400. */
export class BadRequestError extends ApplicationError {
    constructor(message, options = {}) {
        super(ERROR_CODES.INVALID_REQUEST, { message, ...options });
    }
}
/** Raised when the caller is authenticated but not permitted to perform this action. */
export class ForbiddenError extends ApplicationError {
    constructor(message, options = {}) {
        super(ERROR_CODES.FORBIDDEN, { message, ...options });
    }
}
/**
 * Database failure. Not operational: the client cannot fix it by retrying
 * with different input, and it should page someone.
 */
export class DatabaseError extends ApplicationError {
    constructor(options = {}) {
        super(ERROR_CODES.DATABASE_ERROR, {
            message: 'Database operation failed',
            ...options,
            isOperational: false,
        });
    }
}
/** An external system (PMS, gateway, provider) failed or misbehaved. */
export class IntegrationError extends ApplicationError {
    constructor(code = ERROR_CODES.INTEGRATION_FAILED, options = {}) {
        super(code, options);
    }
}
export class PaymentError extends ApplicationError {
    constructor(code = ERROR_CODES.PAYMENT_FAILED, options = {}) {
        super(code, options);
    }
}
export class PmsError extends ApplicationError {
    constructor(code = ERROR_CODES.PMS_UNAVAILABLE, options = {}) {
        super(code, options);
    }
}
/**
 * Invalid or missing configuration. Not operational — a misconfigured service
 * should fail to start rather than serve traffic incorrectly.
 */
export class ConfigurationError extends ApplicationError {
    constructor(options = {}) {
        super(ERROR_CODES.CONFIGURATION_ERROR, {
            message: 'Invalid configuration',
            ...options,
            isOperational: false,
        });
    }
}
export class NetworkError extends ApplicationError {
    constructor(options = {}) {
        super(ERROR_CODES.NETWORK_ERROR, { message: 'Network request failed', ...options });
    }
}
export class TimeoutError extends ApplicationError {
    constructor(code = ERROR_CODES.TIMEOUT, options = {}) {
        super(code, options);
    }
}
/** Type guard for errors this platform raised deliberately. */
export function isApplicationError(error) {
    return error instanceof ApplicationError;
}
/**
 * Wraps an unknown thrown value as an ApplicationError.
 *
 * The catch-all at every service boundary: an unexpected throw becomes a
 * non-operational INTERNAL_ERROR whose message is never leaked to the client.
 */
export function toApplicationError(error, correlationId) {
    if (isApplicationError(error))
        return error;
    return new ApplicationError(ERROR_CODES.INTERNAL_ERROR, {
        message: 'An unexpected error occurred',
        ...(correlationId === undefined ? {} : { correlationId }),
        cause: error,
        isOperational: false,
    });
}
//# sourceMappingURL=application-error.js.map